import { initializeApp } from "firebase/app";
import { doc, getFirestore, setDoc } from "firebase/firestore";
import { useEffect, useRef, useState } from "react";

// Switch FIREBASE_CONFIG to DEV_FIREBASE_CONFIG for development builds
const PROD_FIREBASE_CONFIG = {
  apiKey: "AIzaSyDhHZTZhDzKcZ2K8mdDI2CPlFJRCZKF19s",
  authDomain: "chant2019.firebaseapp.com",
  databaseURL: "https://chant2019.firebaseio.com",
  projectId: "chant2019",
  storageBucket: "chant2019.appspot.com",
  messagingSenderId: "740385069646",
  appId: "1:740385069646:web:3f01893c71b68122",
};

// const DEV_FIREBASE_CONFIG = {
//   apiKey: "AIzaSyCKXdlOfbXdO8ceZIa1MJlVStRdHaD39a8",
//   authDomain: "chantbackup-36104.firebaseapp.com",
//   databaseURL: "https://chantbackup-36104-default-rtdb.firebaseio.com",
//   projectId: "chantbackup-36104",
//   storageBucket: "chantbackup-36104.appspot.com",
//   messagingSenderId: "822302261812",
//   appId: "1:822302261812:web:81c81989cebe5c37efc061",
// };

const FIREBASE_CONFIG = PROD_FIREBASE_CONFIG;

const STORE_URLS = {
  Android:
    "https://play.google.com/store/apps/details?id=com.geekyants.services.chant",
  iOS: "https://apps.apple.com/in/app/chant/id1477159385",
};

let firebaseApp;
function getFirebaseDb() {
  if (!firebaseApp) firebaseApp = initializeApp(FIREBASE_CONFIG);
  return getFirestore(firebaseApp);
}

function detectPlatform() {
  if (/android/i.test(navigator.userAgent)) return "Android";
  if (/iphone|ipad|ipod/i.test(navigator.userAgent)) return "iOS";
  return "Web";
}

function getParams() {
  const url = new URL(window.location.href);
  const data = url.searchParams.get("data");
  if (!data) throw new Error("Missing link parameters");

  // Peek at type for UI decisions only — the blob is passed to the app as-is.
  let type;
  try {
    type = atob(data).split("|")[0];
  } catch {
    throw new Error("Invalid link format");
  }

  if (type !== "register" && type !== "emailAuth") {
    throw new Error("Unrecognized link type");
  }

  return { type, encodedData: data };
}

async function saveToFirestore(params) {
  try {
    const db = getFirebaseDb();
    const res = await fetch("https://api.ipify.org?format=json");
    const { ip } = await res.json();
    await setDoc(doc(db, "deepLinkParams", ip), {
      ...params,
      timestamp: Date.now(),
      expiresAt: Date.now() + 10 * 60 * 1000,
    });
  } catch (err) {
    console.error("Firestore write failed:", err);
  }
}

function App() {
  return (
    <div className="app">
      <header className="topbar" role="banner">
        <span className="wordmark" aria-label="Chant">
          chant
        </span>
      </header>
      <Body />
      <Footer />
    </div>
  );
}

function Body() {
  const [status, setStatus] = useState("loading"); // "loading" | "fallback" | "error"
  const [errorMessage, setErrorMessage] = useState("");
  const [storeUrl, setStoreUrl] = useState("");
  const [linkType, setLinkType] = useState("");
  const platformRef = useRef("");
  const timeoutRef = useRef(null);
  const visibilityHandlerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        const platform = detectPlatform();
        platformRef.current = platform;

        if (platform === "Web") {
          setErrorMessage("Please open this link on a mobile device");
          setStatus("error");
          return;
        }

        const params = getParams();

        // Pass the encoded blob verbatim — the app decodes it via fromUri().
        const deepLinkUrl = `chant://register?data=${encodeURIComponent(params.encodedData)}`;
        window.location.href = deepLinkUrl;

        // If the app opens, the page goes hidden — cancel the fallback timeout
        const onVisibilityChange = () => {
          if (document.hidden) {
            clearTimeout(timeoutRef.current);
            document.removeEventListener(
              "visibilitychange",
              onVisibilityChange,
            );
          }
        };
        visibilityHandlerRef.current = onVisibilityChange;
        document.addEventListener("visibilitychange", onVisibilityChange);

        // After 2s, if the page is still visible the app isn't installed
        timeoutRef.current = setTimeout(async () => {
          document.removeEventListener("visibilitychange", onVisibilityChange);
          if (cancelled) return;

          if (params.type === "register") {
            // Decode fields for Firestore — document shape must stay flat for the app's deferred-install reader.
            const [, groupId, regCode, role] = atob(params.encodedData).split("|");
            saveToFirestore({ type: params.type, groupId, regCode, role });
            setStoreUrl(STORE_URLS[platform]);
          }

          setLinkType(params.type);
          setStatus("fallback");
        }, 2000);
      } catch (err) {
        if (!cancelled) {
          setErrorMessage(err.message);
          setStatus("error");
        }
      }
    };

    run();

    return () => {
      cancelled = true;
      clearTimeout(timeoutRef.current);
      if (visibilityHandlerRef.current) {
        document.removeEventListener(
          "visibilitychange",
          visibilityHandlerRef.current,
        );
      }
    };
  }, []);

  if (status === "error") {
    return (
      <main className="body" role="main" aria-label="Chant deep link redirect">
        <h1 className="error-heading">
          Something
          <br />
          went <span className="accent">wrong.</span>
        </h1>
        <p className="error-message" role="alert" aria-live="polite">
          {errorMessage}
        </p>
      </main>
    );
  }

  if (status === "fallback") {
    if (linkType === "register") {
      return (
        <main className="body" role="main" aria-label="Chant deep link redirect">
          <h1 className="heading">
            Almost <span className="accent">there.</span>
          </h1>
          <p className="subtext">
            Chant isn't installed on this device. Please tap below to install.
          </p>
          <a
            href={storeUrl}
            className="store-btn"
            aria-label={`Open in ${platformRef.current === "iOS" ? "App Store" : "Play Store"}`}
          >
            Open in {platformRef.current === "iOS" ? "App Store" : "Play Store"}
          </a>
        </main>
      );
    }

    // emailAuth fallback — the user clicked a sign-in link but the app didn't
    // open. Don't push them to the store: the email link is single-use and will
    // likely expire by the time they install, so re-requesting from inside the
    // app is the cleanest recovery.
    return (
      <main className="body" role="main" aria-label="Chant deep link redirect">
        <h1 className="heading">
          The app isn't <span className="accent">open.</span>
        </h1>
        <p className="subtext">
          Please open the Chant app — your sign-in should complete
          automatically.
        </p>
        <p className="subtext">
          If it doesn't, go back to the app and request a new sign-in email.
        </p>
      </main>
    );
  }

  return (
    <main className="body" role="main" aria-label="Chant deep link redirect">
      <h1 className="heading">
        Opening
        <br />
        <span className="accent">Chant</span> for you.
      </h1>
      <p className="subtext">Hang tight, we're redirecting you to the app.</p>

      <div
        className="spinner-wrap"
        role="status"
        aria-label="Opening Chant app"
      >
        <div className="spinner" aria-hidden="true" />
        <span className="spinner-label">Connecting...</span>
      </div>
    </main>
  );
}

function Footer() {
  return (
    <footer className="footer" role="contentinfo" aria-label="Footer">
      <p>&copy; {new Date().getFullYear()} Chant. All rights reserved.</p>
    </footer>
  );
}

export default App;
